import React, { useEffect, useRef, useState } from 'react'
import { Priority, TicketDto, TicketStatus } from 'model/request-model'
import { GoTrash } from 'react-icons/go';
import { HiOutlineBriefcase } from 'react-icons/hi2';
import { IoIosArrowRoundBack, IoMdClose } from 'react-icons/io';
import { FloatingLabel, Form, Spinner } from 'react-bootstrap';
import { ItemModel } from 'model/users-model';
import { useGetUsersQuery } from 'api/userManagementApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAssignSingleTicketMutation, useDeleteTicketMutation, useGetChatHistoryByChatIdQuery, useGetTicketByIdQuery, useReplyTicketMutation } from 'api/requestApi';
import { showToastError, showToastSuccess } from 'components/toastify/ToastNotifications';
import { useFormik } from 'formik';
import { singleAssignTicket } from 'validation-schema/ticket-schema';
import { BiSolidCloudUpload } from 'react-icons/bi';
import { FormattedDateTime } from 'components/formatted-date';
import { useSelector } from 'react-redux';
import { getUserData } from 'redux/authSlice';
import { FaFilePdf, FaFileExcel, FaFileWord, FaFileAlt, FaFileImage } from 'react-icons/fa';

type Props = {
    ticketId: string | undefined;
    handleReturn: () => void;
}

const TicketDetails = ({ ticketId, handleReturn }: Props) => {

    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState('New');
    const [assignSingle, setAssignSingle] = useState('');
    const [ticketReply, setTicketReply] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [ticketDetails, setTicketDetails] = useState<TicketDto>();
    const userData = useSelector(getUserData);
    const { data: ticketById, error: ticketError, isFetching: ticketIsFetching, refetch: ticketRefetch } = useGetTicketByIdQuery(ticketId || '');

    useEffect(() => {
        if (ticketIsFetching) {
            setLoading(true);
        }
        else {
            setLoading(false);
            if (ticketError) {
                showToastError("Unable to fetch data from the server");
            } else {
                setTicketDetails(ticketById?.data as TicketDto)
            }
        }
    }, [ticketIsFetching, ticketId]);

    const [adminUsers, setAdminUsers] = useState<ItemModel>();
    const { data: usersData, error: usersError, isFetching: usersIsFetching, refetch: refetchUser } = useGetUsersQuery();
    useEffect(() => {
        if (usersIsFetching) {
            setLoading(true);
        }
        else {
            setLoading(false);
            if (usersError) {
                showToastError("Unable to fetch data from the server");
            } else {
                setAdminUsers(usersData?.data as ItemModel)
            }
        }
    }, [usersIsFetching]);

    const {data: ticketChatHistory, error: ticketChatError, isFetching: fetchingChatHistory } = useGetChatHistoryByChatIdQuery(ticketDetails?.chatId || '', {skip: !ticketDetails?.isChatExist})

    const [replyTicket, replyTicketResult] = useReplyTicketMutation();

    const [deleteTicket] = useDeleteTicketMutation();

    const handleDeleteTicket = async (ticketId: string) => {
        try {
            setDeleteLoading(true);
            const response = await deleteTicket(ticketId).unwrap();
            if (response) {
                showToastSuccess(response?.ResponseMessage);
                handleReturn();
            }
        } catch (error: any) {
            showToastError(error?.data?.ResponseMessage || "Internal server error")
        }
        finally {
            setDeleteLoading(false);
        }
    }

    const [isDragOver, setIsDragOver] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileUpload(e.dataTransfer.files);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        handleFileUpload(files);
    };

    const handleFileUpload = (files: FileList | null) => {
        if (files && files.length > 0) {
            const newAttachments: File[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const reader = new FileReader();
                reader.onload = (event) => {
                    if (event.target && event.target.result) {
                        const blob = new Blob([event.target.result], { type: file.type });
                        const fileWithData = new File([blob], file.name, { type: file.type });
                        newAttachments.push(fileWithData);
                        if (newAttachments.length === files.length) {
                            setAttachments([...attachments, ...newAttachments]);
                        }
                    }
                };
                reader.readAsArrayBuffer(file);
            }
        }
    };

    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const removeAttachment = (indexToRemove: number) => {
        setAttachments(prevAttachments => prevAttachments.filter((_, index) => index !== indexToRemove));
    };

    const handleReply = async () => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('message', replyContent);
            formData.append('ReplyFrom', 'SUPER_ADMIN');
            if (userData?.userId) {
                formData.append('userId', userData.userId);
            }
            //formData.append(`files`, attachments);
            //Append each attachment individually
            attachments.forEach((file, index) => {
                formData.append(`files`, file);
            });
            const response = await replyTicket([ticketDetails?.id || '', formData]).unwrap();
            if (response) {
                showToastSuccess(response?.ResponseMessage);
                setTicketReply(false);
                ticketRefetch();
            }
        }
        catch (error: any) {
            showToastError(error?.data?.ResponseMessage || "Internal server error")
        }
        finally {
            setLoading(false);
        }
    }

    const [assignSingleTicket] = useAssignSingleTicketMutation();

    
    const formik = useFormik({
        initialValues: {
            ticketId: ticketDetails?.id || '',
            ticketStatus: '',
            assignUserId: '',
            dueDate: '',
            priority: ''
        },
        validationSchema: singleAssignTicket,
        onSubmit: async (values) => {
            const model = {...values, ticketId: ticketDetails?.id || ''};
            try {
                setLoading(true);
                const response = await assignSingleTicket(model).unwrap();
                if (response) {
                    showToastSuccess(response?.ResponseMessage);
                    ticketRefetch();
                    setAssignSingle('alreadyAssignedSingle');
                }
            }
            catch (error: any) {
                showToastError(error?.data?.ResponseMessage)
            }
            finally {
                setLoading(false);
            }
        },
    });
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const getFileIcon = (url: string) => {
        const ext = url.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'svg', 'gif'].includes(ext || '')) return <FaFileImage className="text-primary me-2" size={20} />;
        if (ext === 'pdf') return <FaFilePdf className="text-danger me-2" size={20} />;
        if (['xls', 'xlsx', 'csv'].includes(ext || '')) return <FaFileExcel className="text-success me-2" size={20} />;
        if (['doc', 'docx'].includes(ext || '')) return <FaFileWord className="text-primary me-2" size={20} />;
        return <FaFileAlt className="text-secondary me-2" size={20} />;
    };

    const isImage = (url: string) => {
        const ext = url.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'svg', 'gif'].includes(ext || '');
    };

    const renderAttachments = (attachments: string[]) => {
        if (!attachments || attachments.length === 0) return null;
        return (
            <div className='py-4 border-top border-bottom'>
                <span className="fw-bold text-dark mb-2 d-block">Attachments</span>
                <div className="d-flex flex-wrap gap-3">
                    {attachments.map((item, index) => (
                        <div key={index} className="d-flex align-items-center p-2 border rounded bg-light" style={{ minWidth: '200px', maxWidth: '300px' }}>
                            {isImage(item) ? (
                                <a href={item} target="_blank" rel="noopener noreferrer">
                                    <img src={item} alt="attachment" style={{ width: '40px', height: '40px', objectFit: 'cover' }} className="rounded me-2" />
                                </a>
                            ) : (
                                getFileIcon(item)
                            )}
                            <a href={item} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-dark text-truncate small flex-grow-1" title={item.split('/').pop()}>
                                {item.split('/').pop() || 'Download File'}
                            </a>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            {ticketIsFetching || fetchingChatHistory ? <div className='text-center p-5'> <Spinner /> </div> : ticketDetails && <div className='row py-3'>
                <div className='col-lg-7'>
                    <div>
                        <button type='button' onClick={() => handleReturn()} className='btn-secondary-outline px-3 py-2'><IoIosArrowRoundBack size={20} /> Go Back</button>
                        <button type='button' onClick={() => handleDeleteTicket(ticketDetails?.id)} className='btn btn-outline-danger rounded-3 border-0 p-2 mx-3'><GoTrash /> Delete {deleteLoading && <Spinner size='sm' />}</button>
                        <button type='button' onClick={() => setActiveFilter('New')} className='btn-primary px-3 py-2'><HiOutlineBriefcase size={20} /> {ticketDetails?.categoryName}</button>
                        <button type='button' className={`btn ${ticketDetails?.status === 'PENDING' ? 'btn-secondary' : ticketDetails?.status === 'ASSIGNED' ? 'btn-success' : ticketDetails?.status === 'RESOLVED' ? 'btn-primary' : 'btn-danger'} font-size-smm rounded-3 mx-3`}>{ticketDetails?.status}</button>
                    </div>
                    <div className='py-4'>
                        <h5 className='fw-bold text-dark'>{ticketDetails?.subject} </h5>
                        <span className='text-secondary me-3'>{ticketDetails?.customerEmail}</span>   |
                        {/* <span className='primary-text mx-3'>Tickets from this user: 12 </span>  */}
                        <span className='ms-3 text-secondary'>{FormattedDateTime(ticketDetails?.createdAt)}</span>
                    </div>
                    <div className='d-flex gap-3 py-4'>
                        <span className='badge bg-outline-secondary text-secondary p-2 fs-5 rounded-circle' style={{ height: '35px', width: '35px' }}>{ticketDetails?.customerName[0]}</span><span className='my-auto text-dark'>{ticketDetails?.customerName}</span>
                    </div>
                    <div className='py-3'>
                        {(ticketChatHistory && ticketChatHistory?.data && ticketChatHistory?.data?.messages?.length > 0) ? ticketChatHistory?.data?.messages?.map(chat => <div><p dangerouslySetInnerHTML={{ __html: chat?.content }}/></div>) : <div className='py-3 mb-3 text-break' dangerouslySetInnerHTML={{__html: ticketDetails?.message}}></div>}
                        {renderAttachments(ticketDetails?.attachments || [])}
                        <div className='py-4'><h6 className='text-dark'>Replies:</h6>
                            {ticketDetails?.ticketReplies?.length > 0 ? ticketDetails?.ticketReplies?.map(item => <div className='bg-light py-2 px-3 my-2 border rounded-3'>
                                <div className="mb-2">
                                    {renderAttachments(item?.attachments || [])}
                                </div>
                                <div className='py-2 mb-2 text-break' dangerouslySetInnerHTML={{ __html: item?.message }} />
                            </div>) : <div className='my-5'><span className='fs-6 text-dark border-bottom'>No reply yet</span></div>}
                        </div>
                        {ticketReply && <div className='py-4'>
                            <ReactQuill modules={{
                                toolbar: [
                                    ['bold', 'italic', 'underline'],
                                    [{ 'align': [] }], // Alignment
                                    ['link'], // Link, Image, Video
                                ],
                            }} className='rounded-3 w-100' placeholder='Enter text...' theme="snow" value={replyContent} onChange={setReplyContent} />

                            <div
                                className={`my-3 file-upload-container pointer ${isDragOver ? 'drag-over' : ''}`}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={openFileDialog}
                            >
                                <BiSolidCloudUpload size='20' className="mb-2" />
                                <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={handleFileInputChange}
                                    ref={fileInputRef}
                                    multiple
                                    accept=".jpg, .png, .jpeg"
                                />
                                <p>Drag and drop a file here, OR <br /> click to choose a file</p>
                                {attachments && (
                                    <div>
                                        <p>Uploaded File Details:</p>
                                        <div>
                                            {attachments?.map((item, index) => <div className='d-inline mx-2'>
                                                <img src={URL.createObjectURL(item)} height='100px' alt="Uploaded File" />
                                                <button type='button' onClick={() => removeAttachment(index)} className="btn btn-danger btn-sm px-1 py-0"><IoMdClose /></button>
                                            </div>)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className='text-end py-2'><button type='button' onClick={() => handleReply()} className='btn-primary py-2'>Submit {loading && <Spinner size="sm" />}</button></div>
                            {/* <textarea rows={5} className='form-control' placeholder='Reply here'></textarea> */}
                        </div>}
                        <div className='d-flex gap-2'>
                            <button type='button' onClick={() => setAssignSingle('assignSingle')} className={`${assignSingle === 'assignSingle' ? 'btn-primary-outline' : 'btn-secondary-outline'} px-3 py-2`}>Assign Ticket</button>
                            <button type='button' onClick={() => setTicketReply(!ticketReply)} className={`${ticketReply ? 'btn-primary-outline' : 'btn-secondary-outline'} px-3 py-2`}>Reply</button>
                            {/* <button type='button' className='btn-secondary-outline px-3 py-2'>Live Chat</button> */}
                        </div>
                    </div>
                </div>
                <div className='col-lg-1'></div>
                {assignSingle === 'assignSingle' &&
                    <div className='col-lg-4 ps-5'>
                        <Form onSubmit={formik.handleSubmit}>
                            <FloatingLabel controlId="floatingInput" label="Assign Ticket To" className="float-input mt-4 px-0" >
                                <Form.Select aria-label="Floating label select example" name="assignUserId" value={formik?.values?.assignUserId} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className="">
                                    <option value=''>Assign to</option>
                                    {adminUsers?.items?.map((users, key) => <option key={key} value={users?.userId}>{users?.firstName} {users?.lastName}</option>)}
                                </Form.Select>
                                {formik.touched.assignUserId && formik.errors.assignUserId && <span className='text-danger font-size-sm'>{formik.errors.assignUserId}</span>}
                            </FloatingLabel>
                            <FloatingLabel controlId="floatingInput" label="Select Status" className="float-input mt-4 px-0" >
                                <Form.Select aria-label="Floating label select example" name="ticketStatus" value={formik?.values?.ticketStatus} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className="">
                                    <option value=''>Status</option>
                                    {Object.values(TicketStatus).map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </Form.Select>
                                {formik.touched.ticketStatus && formik.errors.ticketStatus && <span className='text-danger font-size-sm'>{formik.errors.ticketStatus}</span>}
                            </FloatingLabel>

                            <FloatingLabel controlId="floatingInput" label="Select Priority" className="float-input mt-4 px-0" >
                                <Form.Select aria-label="Floating label select example" name="priority" value={formik?.values?.priority} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className="">
                                    <option value=''>Priority</option>
                                    {Object.values(Priority).map((priority) => (
                                        <option key={priority} value={priority}>
                                            {priority}
                                        </option>
                                    ))}
                                </Form.Select>
                                {formik.touched.priority && formik.errors.priority && <span className='text-danger font-size-sm'>{formik.errors.priority}</span>}
                            </FloatingLabel>
                            <FloatingLabel controlId="floatingInput" label="Due Date" className="float-input mt-4 px-0" >
                                <Form.Control type="date" min={tomorrow.toISOString().split('T')[0]} name="dueDate" value={formik?.values?.dueDate} onChange={formik?.handleChange} onBlur={formik?.handleBlur} placeholder="Select Date" />{formik.touched.dueDate && formik.errors.dueDate && <span className='text-danger font-size-sm'>{formik.errors.dueDate}</span>}
                            </FloatingLabel>
                            <button type='submit' className='w-100 btn-primary my-3'>Assign Now {loading && <Spinner size='sm' />}</button>
                        </Form>
                    </div>}
                {assignSingle === 'alreadyAssignedSingle' || ticketDetails?.status === 'ASSIGNED' || ticketDetails?.status === 'RESOLVED' &&
                    <div className='col-lg-4 ps-5'>

                        <button className='w-100 btn-orange px-4 text-start'>Ticket assigned to {adminUsers?.items?.filter(user => user?.userId === ticketDetails?.assignedUserId)?.map(user => <span className='fw-bold'>{user?.firstName} {user?.lastName}</span>)}</button>
                        {/* <div className='bg-outline-secondary rounded-4 my-4 p-3 lh-base'> */}
                        {/* {ticketDetails?.ticketActionLogs?.map(item => <div className='p-2'>
                            <span className=''>{item?.actionType}</span>
                            <span className=''>{item?.oldValue}</span>
                            <span className=''>{item?.newValue}</span>
                        </div>)} */}
                        {/* <div className='d-flex gap-2 text-secondary'>
                                <img src={Timeline} className='align-bottom' />
                                <p className='m-0'>Jan 5th, 2022 02:30 <br /><span className='text-dark'>Message received</span><br /> <br />Jan 5th, 2022 02:30 <br /><span className='text-dark'>You assigned to Fatima Bello: SA (Support)</span></p>
                            </div> */}
                        {/* </div> */}
                    </div>}
            </div>}
        </div>
    )
}

export default TicketDetails
